import _ from 'lodash';

export const toggleItemWithShiftCollection = <T extends object>(
  allData: T[],
  checkedItems: T[],
  item: T,
  added: boolean,
  anchor: T | null,
  selectKey: keyof T,
  isShifted?: boolean
) => {
  // We need to add everything between the last checked item and the item if it's shifted
  if (added) {
    if (_.some(checkedItems, (idable) => idable[selectKey] === item[selectKey])) {
      return checkedItems;
    } else {
      if (isShifted && anchor) {
        // Need to get range between the items
        const [min, max] = getRangeBetweenItems(allData, item, anchor, selectKey);
        if (min !== undefined && max !== undefined) {
          // End is not inclusive
          const itemsToAdd = allData.filter((value, index) => _.inRange(index, min, max + 1));
          return _.unionBy(checkedItems, itemsToAdd, (value) => value[selectKey]);
        } else {
          return [...checkedItems, item];
        }
      } else {
        return [...checkedItems, item];
      }
    }
  } else {
    if (isShifted && anchor) {
      const groupings = getGroupings(allData, checkedItems, selectKey);
      const removedItemIndex = _.findIndex(allData, (current) => current[selectKey] === item[selectKey]);
      const activeGrouping = groupings.find((grouping) => {
        const first = _.first(grouping);
        const last = _.last(grouping);

        return first !== undefined && last !== undefined && removedItemIndex >= first && removedItemIndex <= last;
      });

      if (activeGrouping) {
        const anchorIndex = _.findIndex(allData, (current) => current[selectKey] === anchor[selectKey]);
        const first = _.first(activeGrouping);
        const last = _.last(activeGrouping);

        if (first !== undefined && last !== undefined && anchorIndex <= last && anchorIndex >= first) {
          // Same grouping need to remove everything besides the items between anchor and the shift clicked item inclusive
          const anchorBefore = anchorIndex - removedItemIndex < 0;
          const itemsToRemove = activeGrouping
            .filter((overallIndex) =>
              anchorBefore
                ? overallIndex < anchorIndex || overallIndex > removedItemIndex
                : overallIndex > anchorIndex || overallIndex < removedItemIndex
            )
            .map((index) => allData[index]);

          return _.differenceBy(checkedItems, itemsToRemove, (value) => value[selectKey]);
        } else if (first && last && first !== removedItemIndex) {
          // Use first item as the anchor
          const anchorBefore = first - removedItemIndex < 0;
          const itemsToRemove = activeGrouping
            .filter((overallIndex) =>
              anchorBefore
                ? overallIndex < first || overallIndex > removedItemIndex
                : overallIndex > first || overallIndex < removedItemIndex
            )
            .map((index) => allData[index]);

          return _.differenceBy(checkedItems, itemsToRemove, (value) => value[selectKey]);
        } else if (first === removedItemIndex) {
          const itemsToRemove = _.drop(activeGrouping, 1).map((index) => allData[index]);
          return _.differenceBy(checkedItems, itemsToRemove, (value) => value[selectKey]);
        }
      }

      return checkedItems;
    } else {
      return checkedItems.filter((idable) => idable[selectKey] !== item[selectKey]);
    }
  }
};

export const getRangeBetweenItems = <T extends object>(data: T[], itemOne: T, itemTwo: T, selectKey: keyof T) => {
  return data
    .reduce<number[]>((acc, val, index) => {
      if (val[selectKey] === itemOne[selectKey] || val[selectKey] === itemTwo[selectKey]) {
        return [...acc, index];
      } else {
        return acc;
      }
    }, [])
    .sort((a, b) => a - b);
};

export const getGroupings = <T extends object>(data: T[], selectedItems: T[], selectKey: keyof T) => {
  const indices = selectedItems
    .map((item) => _.findIndex(data, (d) => d[selectKey] === item[selectKey]))
    .sort((a, b) => a - b);
  return groupIntegers(indices);
};

export const groupIntegers = (values: number[]) => {
  return values.reduce<number[][]>((acc, val, index) => {
    if (index === 0) {
      return [[val]];
    }

    const lastList = _.last(acc)!;
    const lastEntry = _.last(lastList)!;

    if (val === lastEntry + 1) {
      const dropped = _.dropRight(acc, 1);
      dropped.push([...lastList, val]);
      return dropped;
    } else {
      const accCopy = acc;
      accCopy.push([val]);
      return accCopy;
    }
  }, []);
};
